import { CommonModule, DOCUMENT } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { Subject, filter, map, startWith, takeUntil } from 'rxjs';
import { DevstroupeConfigService } from '@devstroupe/services/config';
import { CompanyService } from 'app/services/company.service';
import { ApiService } from 'app/shared/apis/api.service';
import { StorageKeysService } from 'app/shared/services/storage-keys.service';
import { AutoTranslateDirective } from 'app/shared/directives/auto-translate.directive';
import { AutoTranslatePipe } from 'app/shared/pipes/translate.pipe';
import { RequesterTabBarComponent, RequesterTabId } from '../../components/requester-tab-bar/requester-tab-bar.component';

const TAB_ROUTES: Record<string, RequesterTabId> = {
    '/requester/home': 'home',
    '/requester/request': 'request',
    '/requester/profile': 'profile',
};

type ColorScheme = 'light' | 'dark' | 'auto';

@Component({
    selector: 'requester-layout',
    standalone: true,
    imports: [
        CommonModule,
        RouterOutlet,
        RouterLink,
        MatIconModule,
        MatTooltipModule,
        AutoTranslateDirective,
        AutoTranslatePipe,
        RequesterTabBarComponent,
    ],
    templateUrl: './requester-layout.component.html',
    styleUrls: ['./requester-layout.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class RequesterLayoutComponent implements OnInit, OnDestroy {
    activeTab: RequesterTabId = 'home';
    showTabBar = true;
    companyName = '';
    companyLogo = '';
    initial = '?';
    currentScheme: ColorScheme = 'light';

    readonly tabs: { id: RequesterTabId; label: string; icon: string; route: string }[] = [
        { id: 'home', label: 'Início', icon: 'heroicons_outline:home', route: '/requester/home' },
        { id: 'request', label: 'Nova solicitação', icon: 'heroicons_outline:plus', route: '/requester/request' },
        { id: 'profile', label: 'Conta', icon: 'heroicons_outline:user', route: '/requester/profile' },
    ];

    private readonly destroy$ = new Subject<void>();

    constructor(
        private readonly router: Router,
        private readonly companyService: CompanyService,
        private readonly configService: DevstroupeConfigService,
        private readonly storageKeys: StorageKeysService,
        private readonly cdr: ChangeDetectorRef,
        @Inject(DOCUMENT) private readonly document: Document,
    ) {}

    get themeToggleIcon(): string {
        switch (this.currentScheme) {
            case 'dark':
                return 'heroicons_outline:moon';
            case 'auto':
                return 'heroicons_solid:bolt';
            default:
                return 'heroicons_outline:sun';
        }
    }

    get themeToggleTooltip(): string {
        switch (this.currentScheme) {
            case 'dark':
                return 'Escuro';
            case 'auto':
                return 'Automático';
            default:
                return 'Claro';
        }
    }

    ngOnInit(): void {
        this.document.documentElement.classList.add('requester-app-active');
        this.document.body.classList.add('requester-app-active');

        const savedScheme = localStorage.getItem(this.storageKeys.scheme) as ColorScheme | null;
        if (savedScheme === 'light' || savedScheme === 'dark' || savedScheme === 'auto') {
            this.configService.config = { scheme: savedScheme };
        }

        this.configService.config$.pipe(takeUntil(this.destroy$)).subscribe((config) => {
            this.currentScheme = (config?.scheme as ColorScheme) || 'light';
            this.cdr.markForCheck();
        });

        this.companyService.currentCompany$.pipe(takeUntil(this.destroy$)).subscribe((company) => {
            this.companyName = company?.name || '';
            this.companyLogo = company?.logo || '';
        });

        ApiService.userCurrent.pipe(takeUntil(this.destroy$)).subscribe((user) => {
            this.initial = (user?.name || '?').charAt(0).toUpperCase();
        });

        this.router.events
            .pipe(
                filter((e): e is NavigationEnd => e instanceof NavigationEnd),
                map(() => this.router.url.split('?')[0]),
                startWith(this.router.url.split('?')[0]),
                takeUntil(this.destroy$),
            )
            .subscribe((url) => {
                const path = url.replace(/\/\d+$/, '');
                this.activeTab = TAB_ROUTES[url] || (path.startsWith('/requester/request') ? 'request' : 'home');
                this.showTabBar = !url.includes('/status/');
            });
    }

    ngOnDestroy(): void {
        this.document.documentElement.classList.remove('requester-app-active');
        this.document.body.classList.remove('requester-app-active');
        this.destroy$.next();
        this.destroy$.complete();
    }

    cycleTheme(): void {
        const next: Record<ColorScheme, ColorScheme> = {
            light: 'dark',
            dark: 'auto',
            auto: 'light',
        };
        this.configService.config = { scheme: next[this.currentScheme] };
    }
}
